import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Shield, Search, Loader2, X, ChevronDown, ChevronRight, ScanLine, Download, Image as ImageIcon, ExternalLink } from "lucide-react";
import { api, describeError } from "@/lib/api";
import { availityPayers, curatedPayers, patchInsurance, pgPatientHistory, searchNotes, getNote } from "@/lib/noteClaim";
import { listAnalyses, getFileContent, getAnalysis } from "@/lib/patientDocs";
import { patientOf as pdPatientOf, prettyType } from "@/components/patientdocs/pdUtil";
import LensCard from "@/components/patientdocs/pdLens";

// Inline Insurance panel — one component for Encounter review, Claims Queue, the claim form
// Payer block and the claim workspace. UI-only reshape of the existing calls (search / scan /
// inherit from history / save primary+secondary). No new endpoints, fields or params.

const HAIR = "#E9E9EC", INK = "#141412", GREY = "#6E6E6B", SUB = "#6C6C69", FAINT = "#9B9B97", INDIGO = "#5856D6", SEL = "rgba(88,86,214,.08)", AMBER = "#9A5A00", AMBERBG = "rgba(255,149,0,.12)";
const MONO = "ui-monospace,Menlo,monospace";

const hasRealId = (p) => !!(p && p.payer_id && p.payer_id !== "MISSING" && !p.payer_id_missing);
const stateOf = (ins) => { if (!ins?.payer_name && !hasRealId(ins)) return "none"; if (hasRealId(ins)) return "ok"; return "needs_id"; };
const money = (v) => { const n = Number(v); return isFinite(n) && v != null && v !== "" ? `$${n.toFixed(2)}` : null; };
const fileToB64 = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).replace(/^data:[^;]+;base64,/, "")); r.onerror = rej; r.readAsDataURL(file); });
const parseD = (s) => { const t = s ? Date.parse(s) : NaN; return isNaN(t) ? null : t; };
const fmtDate = (iso) => { const d = iso ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso) : null; return d && !isNaN(d) ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""; };
const fmtMonth = (t) => { const d = new Date(t); return isNaN(d) ? "" : d.toLocaleDateString("en-US", { month: "short", year: "numeric" }); };
const isPdf = (ct) => String(ct || "").includes("pdf");

const PANEL_CSS = `.insp{container-type:inline-size}
.insp-b{display:grid;grid-template-columns:280px minmax(0,1fr);gap:22px;padding:16px}
@container (max-width:820px){.insp-b{grid-template-columns:minmax(0,1fr)}.insp-cardcol{order:2}.insp-main{order:1}.insp-strip{display:none!important}}`;

// ── payer search (first list + widen) ───────────────────────────────────────
function PayerSearch({ prefill, onPick }) {
  const [q, setQ] = useState(prefill || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wide, setWide] = useState(null); // null | "loading" | rows[]
  const [curated, setCurated] = useState([]);
  const tRef = useRef(null);
  useEffect(() => { let a = true; curatedPayers().then((c) => a && setCurated(c)).catch(() => {}); return () => { a = false; }; }, []);
  useEffect(() => {
    const term = q.trim(); setWide(null);
    if (term.length < 2) { setItems([]); return undefined; }
    setLoading(true); clearTimeout(tRef.current);
    tRef.current = setTimeout(async () => {
      let out = []; try { out = await availityPayers(term, 6); } catch { out = []; }
      setItems(out); setLoading(false);
    }, 280);
    return () => clearTimeout(tRef.current);
  }, [q]);
  const showMore = async () => {
    const term = q.trim(); setWide("loading");
    const first = term.split(/\s+/)[0];
    let wa = []; try { wa = await availityPayers(first, 50); } catch { wa = []; }
    const cur = curated.map((p) => ({ name: p.name, payer_id: String(p.payer_id ?? p.id ?? "") }));
    const seen = new Set(items.map((p) => String(p.payer_id ?? p.id)));
    const words = term.toLowerCase().split(/\s+/).filter(Boolean);
    const score = (n) => words.reduce((s, w) => s + (String(n || "").toLowerCase().includes(w) ? 1 : 0), 0);
    const merged = [...wa, ...cur].filter((p) => { const id = String(p.payer_id ?? p.id); if (seen.has(id)) return false; seen.add(id); return true; })
      .map((p) => ({ name: p.name, payer_id: String(p.payer_id ?? p.id ?? ""), s: score(p.name) }))
      .sort((a, b) => b.s - a.s).slice(0, 8);
    setWide(merged);
  };
  const term = q.trim();
  return (
    <div data-testid="insp-payer-search">
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: FAINT }} />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search payers by name or id"
          className="w-full text-[13px] pl-8 pr-2 rounded-[9px] outline-none" style={{ height: 40, border: `1px solid ${INDIGO}`, boxShadow: "0 0 0 3px rgba(88,86,214,.14)" }} data-testid="insp-payer-input" />
      </div>
      {term.length >= 2 ? (
        <div className="mt-1.5 rounded-[9px] overflow-hidden" style={{ border: `1px solid ${HAIR}` }}>
          <div className="max-h-[260px] overflow-y-auto">
            {loading ? <div className="px-3 py-2 text-[12px] inline-flex items-center gap-1.5" style={{ color: FAINT }}><Loader2 size={12} className="animate-spin" />Searching…</div>
              : items.length ? items.map((p, i) => (
                <button key={i} type="button" onClick={() => onPick({ payer_id: String(p.payer_id ?? p.id ?? ""), payer_name: p.name })} className="w-full flex items-center gap-2 text-left px-3 py-2 hover:bg-black/[.04]" data-testid={`insp-payer-opt-${i}`}>
                  <span className="text-[12.5px] font-semibold flex-1 min-w-0 truncate" style={{ color: INK }}>{p.name}</span>
                  <span className="text-[11px]" style={{ color: SUB, fontFamily: MONO }}>{p.payer_id ?? p.id}</span>
                </button>
              )) : <div className="px-3 py-2 text-[12px]" style={{ color: GREY }}>No exact match for “{term}”.</div>}
            {wide === "loading" ? <div className="px-3 py-2 text-[12px] inline-flex items-center gap-1.5" style={{ color: FAINT }}><Loader2 size={12} className="animate-spin" />Widening the match…</div>
              : Array.isArray(wide) ? (
                wide.length ? (
                  <>
                    <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-[.05em]" style={{ color: FAINT, background: "#FAFAFC" }}>Wider matches · {wide.length}</div>
                    {wide.map((p, i) => (
                      <button key={`w${i}`} type="button" onClick={() => onPick({ payer_id: p.payer_id, payer_name: p.name })} className="w-full flex items-center gap-2 text-left px-3 py-2 hover:bg-black/[.04]" data-testid={`insp-payer-wide-${i}`}>
                        <span className="text-[12.5px] flex-1 min-w-0 truncate" style={{ color: INK }}>{p.name}</span>
                        <span className="text-[11px]" style={{ color: SUB, fontFamily: MONO }}>{p.payer_id}</span>
                      </button>
                    ))}
                    <div className="px-3 py-1.5 text-[11px]" style={{ color: GREY }}>That’s every close match. Not here? Search by payer id.</div>
                  </>
                ) : <div className="px-3 py-2 text-[12px]" style={{ color: GREY }}>No payer matches “{term}”. Try the payer id, or fewer words.</div>
              ) : (
                <button type="button" onClick={showMore} className="w-full text-left px-3 py-2 text-[12px] font-semibold" style={{ color: INDIGO }} data-testid="insp-payer-more">Show more payer matches</button>
              )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── newest insurance card column ─────────────────────────────────────────────
function CardColumn({ clientId, mrn, patientName, editing, onScanFill }) {
  const [rows, setRows] = useState(null);
  const [img, setImg] = useState(null); // dataUrl of newest
  const [showAll, setShowAll] = useState(false);
  const [full, setFull] = useState(null);
  const [others, setOthers] = useState(false);
  const scanRef = useRef(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!clientId || (!mrn && !patientName)) { setRows([]); return undefined; }
    setRows(null); setImg(null); setFull(null); setShowAll(false); setOthers(false);
    listAnalyses({ client_id: clientId, status: "completed", doc_type: "id_card", limit: 500, etag: true })
      .then((r) => (r.analyses || []))
      .then((list) => {
        const mine = list.filter((a) => { const p = pdPatientOf(a); if (mrn && p.mrn) return String(p.mrn) === String(mrn); if (patientName) return String(p.name || "").toLowerCase() === String(patientName).toLowerCase(); return false; })
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        if (alive) setRows(mine);
      }).catch(() => alive && setRows([]));
    return () => { alive = false; };
  }, [clientId, mrn, patientName]);

  const newest = rows && rows.length ? rows[0] : null;
  useEffect(() => {
    let alive = true;
    if (!newest) { setImg(null); return undefined; }
    const docId = newest.document_id || newest.id;
    getFileContent(docId).then((f) => { if (alive) setImg({ dataUrl: `data:${f.content_type};base64,${f.base64 || f.file_content}`, ct: f.content_type }); }).catch(() => {});
    getAnalysis(newest.id).then((a) => alive && setFull(a)).catch(() => {});
    return () => { alive = false; };
  }, [newest]);

  const onScan = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setScanning(true);
    try {
      const b64 = await fileToB64(f);
      const { data } = await api.post(`/api/edi/note-to-claim/notes/${onScanFill.noteId}/scan-insurance-card`, { file_base64: b64, filename: f.name });
      const ins = data?.insurance || {};
      onScanFill.fill({ payer_id: ins.payer_id, payer_name: ins.payer_name, member_id: ins.member_id, group_number: ins.group_number });
      toast.success("Card read — payer, member and group filled in.");
    } catch (ex) { toast.error(describeError(ex)); } finally { setScanning(false); }
  };
  const viewImage = () => { if (!img?.dataUrl) return; const w = window.open(); if (w) w.document.write(`<iframe src="${img.dataUrl}" style="border:0;position:fixed;inset:0;width:100%;height:100%"></iframe>`); };
  const download = () => { if (!img?.dataUrl) return; const a = document.createElement("a"); a.href = img.dataUrl; a.download = newest?.filename || "insurance-card"; document.body.appendChild(a); a.click(); a.remove(); };

  const ex = full?.extraction || full?.result || newest?.extraction || {};
  const kv = [["Payer", ex.payer_name || ex.payer], ["Member ID", ex.member_id || ex.member], ["Plan", ex.plan || ex.plan_name], ["Group", ex.group_number || ex.group]];

  if (rows == null) return <div className="insp-cardcol text-[12px] inline-flex items-center gap-1.5" style={{ color: GREY }} data-testid="insp-card-loading"><Loader2 size={12} className="animate-spin" />Loading card…</div>;

  return (
    <div className="insp-cardcol" data-testid="insp-cardcol">
      <input ref={scanRef} type="file" accept="image/png,image/jpeg,image/jpg,application/pdf" className="hidden" onChange={onScan} data-testid="insp-scan-input" />
      {newest ? (
        <>
          <div className="text-[10px] font-bold uppercase tracking-[.06em] mb-1.5" style={{ color: FAINT }}>Newest insurance card</div>
          <div className="rounded-[12px] border overflow-hidden bg-white" style={{ borderColor: HAIR }}>
            <div className="grid place-items-center relative" style={{ height: 150, background: "#F1F1F4" }}>
              {!img ? <Loader2 size={15} className="animate-spin" style={{ color: GREY }} />
                : isPdf(img.ct) ? <iframe title="card" src={`${img.dataUrl}#toolbar=0&view=FitH`} style={{ width: "100%", height: 150, border: 0 }} />
                  : <img alt="insurance card" src={img.dataUrl} style={{ maxWidth: "100%", maxHeight: 150, objectFit: "contain" }} />}
              <button type="button" onClick={viewImage} className="absolute bottom-1.5 right-1.5 text-[10.5px] font-semibold rounded-full px-2 py-1 inline-flex items-center gap-1" style={{ background: "rgba(20,20,18,.78)", color: "#fff" }} data-testid="insp-card-viewimg"><ImageIcon size={11} />View image</button>
            </div>
            <div className="px-3 py-2">
              <div className="flex items-center gap-2 text-[11px]" style={{ color: SUB }}>
                <span className="truncate flex-1">{newest.filename} · {fmtDate(newest.created_at)}</span>
                <button type="button" onClick={download} className="font-semibold inline-flex items-center gap-1" style={{ color: INDIGO }} data-testid="insp-card-download"><Download size={11} />Download</button>
              </div>
              <div className="mt-2 space-y-1">
                {kv.map(([k, v]) => <div key={k} className="flex items-center gap-2 text-[12px]"><span style={{ color: GREY, width: 72 }}>{k}</span><span className="flex-1 truncate" style={{ color: INK, fontFamily: k === "Member ID" ? MONO : undefined }}>{v || <span style={{ color: FAINT }}>Not on card</span>}</span></div>)}
              </div>
              <div className="flex items-center gap-3 mt-2 pt-2 flex-wrap" style={{ borderTop: `1px solid ${HAIR}` }}>
                <button type="button" onClick={() => setShowAll((v) => !v)} className="text-[11.5px] font-semibold" style={{ color: INDIGO }} data-testid="insp-card-allfields">{showAll ? "Hide fields" : "View all fields"}</button>
                {rows.length > 1 ? <button type="button" onClick={() => setOthers((v) => !v)} className="text-[11.5px] font-semibold" style={{ color: SUB }} data-testid="insp-card-others">Other cards ({rows.length - 1})</button> : null}
                <button type="button" onClick={() => scanRef.current?.click()} disabled={scanning} className="text-[11.5px] font-semibold inline-flex items-center gap-1" style={{ color: SUB }} data-testid="insp-card-scannew">{scanning ? <Loader2 size={11} className="animate-spin" /> : <ScanLine size={11} />}Scan new</button>
              </div>
            </div>
          </div>
          {showAll ? <div className="mt-2" data-testid="insp-card-fullfields"><LensCard analysis={full || newest} /></div> : null}
          {others ? (
            <div className="mt-2 space-y-1" data-testid="insp-card-otherlist">
              {rows.slice(1).map((r) => <div key={r.id} className="text-[11.5px] rounded-[8px] border px-2.5 py-1.5" style={{ borderColor: HAIR, color: SUB }}>{prettyType(r.document_type)} · {fmtDate(r.created_at)}</div>)}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className="rounded-[12px] border border-dashed grid place-items-center text-center px-3" style={{ borderColor: HAIR, minHeight: 150 }} data-testid="insp-card-none">
            <div>
              <ScanLine size={20} className="mx-auto mb-1.5" style={{ color: GREY }} />
              <div className="text-[12.5px] font-semibold" style={{ color: INK }}>No insurance card on file</div>
              <div className="text-[11.5px] mt-0.5" style={{ color: GREY }}>{editing ? "You don’t need one. Search the payer." : "Scan one to keep it with the chart."}</div>
            </div>
          </div>
          <button type="button" onClick={() => scanRef.current?.click()} disabled={scanning} className="mt-2 text-[11.5px] font-semibold rounded-[8px] px-3 py-1.5 border inline-flex items-center gap-1.5" style={{ borderColor: HAIR, color: INK }} data-testid="insp-card-scan"><ScanLine size={12} />Scan a card</button>
        </>
      )}
    </div>
  );
}

// ── payer history ─────────────────────────────────────────────────────────
function useHistory(clientId, mrn) {
  const [visits, setVisits] = useState(null);
  const [ehi, setEhi] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!mrn) { setVisits([]); setEhi({ bound: false, claims: [] }); return undefined; }
    searchNotes({ client_id: String(clientId), mrn, limit: 50 }).then((r) => alive && setVisits(r.notes || [])).catch(() => alive && setVisits([]));
    pgPatientHistory(clientId, mrn).then((d) => alive && setEhi(d)).catch(() => alive && setEhi({ bound: false, claims: [] }));
    return () => { alive = false; };
  }, [clientId, mrn]);
  return { visits, ehi };
}

const claimPaid = (c) => (c.lines || []).reduce((s, l) => s + (Number(l.ins_payment) || 0), 0);
const isPaidClaim = (c) => (c.lines || []).some((l) => Number(l.ins_payment) > 0);
const AWAIT_RE = /AWAIT|OPEN|PEND/i;

function PayerHistory({ clientId, mrn, excludeId, visitDos, visitCpts, secondaryMode, current, onUse, onInherit }) {
  const { visits, ehi } = useHistory(clientId, mrn);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("payer");
  const [flatN, setFlatN] = useState(20);

  const claims = (ehi?.claims) || [];
  const otherVisits = useMemo(() => (visits || []).filter((v) => v.id !== excludeId && v.insurance?.payer_name), [visits, excludeId]);
  const vt = parseD(visitDos);

  // closest item (across both sources) by |dos - visit dos|
  const closest = useMemo(() => {
    if (vt == null) return null;
    let best = null, bd = Infinity;
    claims.forEach((c) => { const t = parseD(c.dos); if (t == null) return; const d = Math.abs(t - vt); if (d < bd) { bd = d; best = { kind: "ehi", key: `${c.encno}|${c.payer_id}`, c }; } });
    otherVisits.forEach((v) => { const t = parseD(v.date_of_service); if (t == null) return; const d = Math.abs(t - vt); if (d < bd) { bd = d; best = { kind: "visit", key: v.id, v }; } });
    return best;
  }, [claims, otherVisits, vt]);

  // shared axis min
  const axisMin = useMemo(() => {
    const ds = [...claims.map((c) => parseD(c.dos)), ...otherVisits.map((v) => parseD(v.date_of_service))].filter((x) => x != null);
    return ds.length ? Math.min(...ds) : null;
  }, [claims, otherVisits]);
  const axisMax = vt != null ? vt : (claims.length ? Math.max(...claims.map((c) => parseD(c.dos) || 0)) : Date.now());

  // group EHI by payer_id|policy_no
  const groups = useMemo(() => {
    const m = new Map();
    claims.forEach((c) => { const k = `${c.payer_id || "—"}|${c.policy_no || "—"}`; if (!m.has(k)) m.set(k, { key: k, payer_id: c.payer_id, payer_name: c.payer_name, policy_no: c.policy_no, claims: [], visits: [] }); m.get(k).claims.push(c); });
    otherVisits.forEach((v) => { const k = `${v.insurance?.payer_id || "—"}|${v.insurance?.member_id || "—"}`; if (!m.has(k)) m.set(k, { key: k, payer_id: v.insurance?.payer_id, payer_name: v.insurance?.payer_name, policy_no: v.insurance?.member_id, claims: [], visits: [] }); m.get(k).visits.push(v); });
    const arr = [...m.values()];
    arr.forEach((g) => {
      g.claims.sort((a, b) => (parseD(b.dos) || 0) - (parseD(a.dos) || 0));
      g.visits.sort((a, b) => (parseD(b.date_of_service) || 0) - (parseD(a.date_of_service) || 0));
      const allD = [...g.claims.map((c) => parseD(c.dos)), ...g.visits.map((v) => parseD(v.date_of_service))].filter((x) => x != null);
      g.max = allD.length ? Math.max(...allD) : -Infinity;
    });
    arr.sort((a, b) => b.max - a.max);
    return arr;
  }, [claims, otherVisits]);

  const curKey = secondaryMode ? current?.secondary : current?.primary;
  const nPolicies = groups.length;
  const closestDate = closest ? fmtDate(closest.kind === "ehi" ? closest.c.dos : closest.v.date_of_service) : "—";

  const summary = (g) => {
    const paidClaims = g.claims.filter(isPaidClaim);
    const paidTotal = g.claims.reduce((s, c) => s + claimPaid(c), 0);
    const latestPaid = paidClaims.length ? fmtDate(paidClaims[0].dos) : null;
    const awaiting = g.claims.filter((c) => !isPaidClaim(c) && AWAIT_RE.test((c.lines || [])[0]?.ar_status || c.ar_status || ""));
    const unpaid = g.claims.filter((c) => !isPaidClaim(c) && !AWAIT_RE.test((c.lines || [])[0]?.ar_status || c.ar_status || ""));
    const newestUnpaid = g.claims[0] && !isPaidClaim(g.claims[0]) && unpaid.includes(g.claims[0]);
    // per current-visit CPT rates
    const codeRates = (visitCpts || []).map((cpt) => {
      let p = 0, n = 0, sum = 0;
      g.claims.forEach((c) => (c.lines || []).forEach((l) => { if (String(l.cpt) === String(cpt)) { n += 1; if (Number(l.ins_payment) > 0) { p += 1; sum += Number(l.ins_payment); } } }));
      return n ? { cpt, p, n, avg: p ? sum / p : 0 } : null;
    }).filter(Boolean);
    return { paidClaims, paidTotal, latestPaid, awaiting, unpaid, newestUnpaid, codeRates };
  };

  const Strip = ({ g }) => {
    if (axisMin == null || axisMax <= axisMin) return null;
    const W = 200, H = 18; const x = (t) => 2 + ((t - axisMin) / (axisMax - axisMin)) * (W - 4);
    return (
      <svg className="insp-strip" width={W} height={H} style={{ flex: "none" }} data-testid={`insp-strip-${g.key}`}>
        <line x1={2} y1={H - 4} x2={W - 2} y2={H - 4} stroke={HAIR} strokeWidth={1} />
        {g.claims.map((c, i) => { const t = parseD(c.dos); if (t == null) return null; const paid = isPaidClaim(c), aw = !paid && AWAIT_RE.test((c.lines || [])[0]?.ar_status || ""); return <circle key={`c${i}`} cx={x(t)} cy={H - 4} r={2.7} fill={paid ? INK : aw ? "#E0A030" : "#fff"} stroke={paid ? INK : aw ? "#E0A030" : "#C9C9CC"} strokeWidth={paid ? 0 : 1} />; })}
        {g.visits.map((v, i) => { const t = parseD(v.date_of_service); if (t == null) return null; return <rect key={`v${i}`} x={x(t) - 1} y={H - 13} width={2} height={10} fill="#C9C9CC" />; })}
        {vt != null ? <line x1={W - 2} y1={2} x2={W - 2} y2={H - 2} stroke={INK} strokeWidth={1.5} /> : null}
      </svg>
    );
  };

  const flat = useMemo(() => {
    const rows = [];
    claims.forEach((c) => rows.push({ kind: "ehi", t: parseD(c.dos), dos: c.dos, encno: c.encno, payer_name: c.payer_name, payer_id: c.payer_id, policy_no: c.policy_no, paid: isPaidClaim(c), paidAmt: claimPaid(c), aw: AWAIT_RE.test((c.lines || [])[0]?.ar_status || ""), c }));
    otherVisits.forEach((v) => rows.push({ kind: "visit", t: parseD(v.date_of_service), dos: v.date_of_service, payer_name: v.insurance?.payer_name, payer_id: v.insurance?.payer_id, policy_no: v.insurance?.member_id, sec: v.insurance_secondary, v }));
    return rows.sort((a, b) => (b.t || 0) - (a.t || 0));
  }, [claims, otherVisits]);

  const useBtnLabel = secondaryMode ? "Use as secondary" : "Use as primary";
  const usePolicy = (g) => onUse({ payer_id: g.payer_id, payer_name: g.payer_name, member_id: g.policy_no, source: "history", chip: "From payer history" });
  const applyRow = (r) => onUse({ payer_id: r.payer_id, payer_name: r.payer_name, member_id: r.policy_no, source: "history", chip: "From payer history" });

  if (visits == null || ehi == null) return <div className="text-[12px] inline-flex items-center gap-1.5 py-2" style={{ color: GREY }}><Loader2 size={12} className="animate-spin" />Loading payer history…</div>;
  const nothing = !claims.length && !otherVisits.length;

  return (
    <div className="mt-4 rounded-[12px] border" style={{ borderColor: HAIR }} data-testid="insp-history">
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1.5" data-testid="insp-history-toggle" aria-expanded={open}>
          <ChevronRight size={14} style={{ color: GREY, transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }} />
          <span className="text-[10.5px] font-bold uppercase tracking-[.06em]" style={{ color: GREY }}>Payer history</span>
        </button>
        <span className="text-[11px]" style={{ color: FAINT }}>
          {ehi?.bound === false && !otherVisits.length ? "No live EHI connection for this client." : nothing ? "No past claims or earlier visits for this patient yet." : `${claims.length} EHI claims · ${otherVisits.length} revQ visits · ${nPolicies} payer policies · closest ${closestDate}`}
        </span>
        <span className="flex-1" />
        {open && !nothing ? (
          <div className="inline-flex items-center rounded-[8px] p-[2px]" style={{ background: "#F1F1F4" }} role="radiogroup">
            {[["payer", "By payer & policy"], ["flat", "Flat"]].map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={view === k} onClick={() => setView(k)} data-testid={`insp-hist-view-${k}`}
                className="text-[11px] font-semibold rounded-[6px] px-2 py-1" style={view === k ? { background: "#fff", color: INK, boxShadow: "0 1px 2px rgba(0,0,0,.08)" } : { background: "transparent", color: SUB }}>{l}</button>
            ))}
          </div>
        ) : !nothing ? <button type="button" onClick={() => setOpen(true)} className="text-[11.5px] font-semibold" style={{ color: INDIGO }} data-testid="insp-history-show">Show</button> : null}
      </div>

      {open && !nothing ? (
        <div className="px-3 pb-3" data-testid="insp-history-body">
          {axisMin != null ? (
            <div className="flex items-center justify-between text-[10px] mb-2 insp-strip" style={{ color: FAINT }}>
              <span>{fmtMonth(axisMin)}</span>
              <span className="inline-flex items-center gap-2"><span>● ins paid</span><span style={{ color: "#E0A030" }}>● awaiting</span><span>○ not paid</span><span>| revQ visit</span></span>
              <span>This visit</span>
            </div>
          ) : null}

          {view === "payer" ? (
            <div className="flex flex-col gap-2.5">
              {groups.map((g) => {
                const hot = closest && (closest.key === g.key || g.claims.some((c) => `${c.encno}|${c.payer_id}` === closest.key) || g.visits.some((v) => v.id === closest.key));
                const s = summary(g);
                const isCur = curKey && hasRealId(g) && String(curKey.payer_id) === String(g.payer_id);
                return <GroupCard key={g.key} g={g} s={s} hot={hot} isCur={isCur} Strip={Strip} secondaryMode={secondaryMode} useBtnLabel={useBtnLabel} onUsePolicy={usePolicy} onInherit={onInherit} />;
              })}
              <HistFooter />
            </div>
          ) : (
            <div>
              <div className="rounded-[10px] border overflow-hidden" style={{ borderColor: HAIR }}>
                {flat.slice(0, flatN).map((r, i) => {
                  const hot = closest && (closest.kind === r.kind && (r.kind === "ehi" ? `${r.c.encno}|${r.c.payer_id}` === closest.key : r.v.id === closest.key));
                  return (
                    <div key={i} className="grid items-center gap-2 px-2.5 py-2 text-[12px]" style={{ gridTemplateColumns: "88px 1fr 90px 92px 110px", borderTop: i ? `1px solid ${HAIR}` : "none", background: hot ? SEL : "transparent" }} data-testid={`insp-flat-${i}`}>
                      <div><div style={{ color: INK }}>{fmtDate(r.dos) || "—"}</div><div className="text-[10px]" style={{ color: FAINT }}>{r.kind === "ehi" ? `Enc ${r.encno || "—"}` : "revQ visit"}{hot ? " · Closest" : ""}</div></div>
                      <div className="min-w-0"><div className="truncate" style={{ color: INK }}>{r.payer_name || "—"}</div>{r.kind === "visit" && r.sec?.payer_name ? <div className="text-[10px] truncate" style={{ color: FAINT }}>+ 2° {r.sec.payer_name}</div> : null}</div>
                      <div className="truncate" style={{ color: SUB, fontFamily: MONO }}>{r.policy_no || "—"}</div>
                      <div style={{ color: r.kind === "visit" ? FAINT : r.paid ? "#1F7A4D" : AMBER }}>{r.kind === "visit" ? "—" : r.paid ? money(r.paidAmt) : r.aw ? "Awaiting" : "Not paid"}</div>
                      <div className="text-right">{hasRealId(r) ? <button type="button" onClick={() => (r.kind === "visit" ? onInherit(r.v, false) : applyRow(r))} className="text-[11px] font-bold rounded-[6px] px-2 py-1 border bg-white" style={{ borderColor: HAIR, color: INK }} data-testid={`insp-flat-use-${i}`}>{r.kind === "visit" ? "Inherit" : useBtnLabel}</button> : <span className="text-[10px]" style={{ color: GREY }}>no id</span>}</div>
                    </div>
                  );
                })}
              </div>
              {flat.length > flatN ? <button type="button" onClick={() => setFlatN((n) => n + 20)} className="mt-2 text-[12px] font-semibold" style={{ color: INDIGO }} data-testid="insp-flat-more">Show 20 more</button> : null}
              <HistFooter />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function HistFooter() {
  return <div className="text-[11px] mt-1 pt-2" style={{ color: FAINT, borderTop: `1px solid ${HAIR}` }}>Full claim lines and balances live in the chart. · <span style={{ color: INDIGO, fontWeight: 600 }}>See full claims in the chart</span></div>;
}

function GroupCard({ g, s, hot, isCur, Strip, secondaryMode, useBtnLabel, onUsePolicy, onInherit }) {
  const [exp, setExp] = useState(false);
  const months = (() => { const ds = [...g.claims.map((c) => parseD(c.dos)), ...g.visits.map((v) => parseD(v.date_of_service))].filter((x) => x != null); if (!ds.length) return ""; return `${fmtMonth(Math.min(...ds))} – ${fmtDate(new Date(Math.max(...ds)).toISOString())}`; })();
  return (
    <div className="rounded-[12px] border overflow-hidden" style={{ borderColor: hot ? INDIGO : HAIR, boxShadow: hot ? `0 0 0 2px ${INDIGO}22` : "none" }} data-testid={`insp-group-${g.key}`}>
      <div className="flex items-start gap-2.5 px-3 py-2.5" style={{ background: "#FBFBFD" }}>
        <button type="button" onClick={() => setExp((v) => !v)} className="mt-0.5" data-testid={`insp-group-toggle-${g.key}`}><ChevronDown size={14} style={{ color: GREY, transform: exp ? "none" : "rotate(-90deg)", transition: "transform .15s" }} /></button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-bold" style={{ color: INK }}>{g.payer_name || "Payer"}</span>
            <span className="text-[11px]" style={{ color: SUB, fontFamily: MONO }}>{g.payer_id || "—"}</span>
            {g.policy_no ? <span className="text-[11px]" style={{ color: SUB }}>policy {g.policy_no}</span> : null}
            {hot ? <span className="text-[10px] font-bold rounded-full px-2 py-0.5" style={{ background: INDIGO, color: "#fff" }}>Closest to this visit</span> : null}
          </div>
          <div className="text-[12px] mt-0.5" style={{ color: FAINT }}>{g.claims.length} claims · {g.visits.length} revQ visits{months ? ` · ${months}` : ""}</div>
          <div className="text-[12px] mt-1 flex flex-wrap items-center" style={{ color: SUB, columnGap: 14, rowGap: 4 }}>
            {g.claims.length ? <span><b style={{ color: INK, fontWeight: 600 }}>Ins paid {s.paidClaims.length} of {g.claims.length}</b>{s.paidTotal ? ` · ${money(s.paidTotal)}` : ""}</span> : null}
            {s.latestPaid ? <span>Latest paid service {s.latestPaid}</span> : null}
            {s.codeRates.map((r) => <span key={r.cpt}>{r.p === 0 ? <span style={{ color: "#E0A030" }}>● </span> : null}{r.cpt} paid {r.p} of {r.n}{r.avg ? ` · avg ${money(r.avg)}` : ""}</span>)}
            {s.awaiting.length ? <span style={{ color: "#E0A030" }}>● {s.awaiting.length} awaiting insurance</span> : null}
            {s.unpaid.length ? <span style={s.newestUnpaid ? { color: "#E0A030" } : undefined}>{s.unpaid.length} unpaid · {((g.claims.find((c) => s.unpaid.includes(c))?.lines || [])[0]?.ar_status || "").toLowerCase()}{s.newestUnpaid ? " (latest claim)" : ""}</span> : null}
          </div>
        </div>
        <Strip g={g} />
        <div className="flex-none">
          {isCur ? <span className="text-[11px] rounded-full px-2 py-1" style={{ background: "#F1F1F4", color: GREY }}>{secondaryMode ? "Current secondary" : "Current primary"}</span>
            : hasRealId(g) ? <button type="button" onClick={() => onUsePolicy(g)} className="text-[11px] font-bold rounded-[7px] px-2.5 py-1.5 border bg-white" style={{ borderColor: HAIR, color: INK }} data-testid={`insp-group-use-${g.key}`}>{useBtnLabel}</button> : <span className="text-[10.5px]" style={{ color: GREY }}>no id</span>}
        </div>
      </div>
      {exp ? (
        <div className="px-3 py-2.5 border-t" style={{ borderColor: HAIR, background: "#FCFCFD" }} data-testid={`insp-group-exp-${g.key}`}>
          {g.claims.length ? (
            <>
              <div className="text-[10px] font-bold uppercase tracking-[.05em] mb-1.5" style={{ color: FAINT }}>Latest claims</div>
              {g.claims.slice(0, 3).map((c, i) => {
                const cpts = [...new Set((c.lines || []).map((l) => l.cpt).filter(Boolean))];
                const paid = isPaidClaim(c), aw = AWAIT_RE.test((c.lines || [])[0]?.ar_status || "");
                return (
                  <div key={i} className="flex items-center gap-2 py-1 text-[12px] flex-wrap" data-testid={`insp-claim-${g.key}-${i}`}>
                    <span style={{ color: INK }}>{fmtDate(c.dos) || "—"}</span>
                    <span className="text-[10.5px]" style={{ color: FAINT }}>Enc {c.encno || "—"}</span>
                    {cpts.slice(0, 5).map((x) => <span key={x} className="text-[10px] rounded-[5px] px-1 py-0.5 border" style={{ borderColor: HAIR, color: SUB, fontFamily: MONO }}>{x}</span>)}
                    <span style={{ color: SUB }}>{money(c.total_billed) || "—"} billed</span>
                    <span style={{ color: paid ? "#1F7A4D" : "#E0A030" }}>{paid ? `${money(claimPaid(c))} ins paid` : aw ? "Awaiting ins" : "Not paid"}</span>
                  </div>
                );
              })}
            </>
          ) : null}
          {g.visits.length ? (
            <>
              <div className="text-[10px] font-bold uppercase tracking-[.05em] mb-1.5 mt-2" style={{ color: FAINT }}>revQ visits on this policy</div>
              {g.visits.map((v) => {
                const sec = v.insurance_secondary?.payer_name;
                return (
                  <div key={v.id} className="flex items-center gap-2 py-1 text-[12px]" data-testid={`insp-visit-${v.id}`}>
                    <span style={{ color: INK }}>{fmtDate(v.date_of_service) || "—"}</span>
                    <span className="text-[10.5px]" style={{ color: FAINT }}>revQ visit</span>
                    {sec ? <span style={{ color: SUB }}>+ 2° {sec} {v.insurance_secondary?.payer_id || ""}</span> : <span style={{ color: FAINT }}>Primary only</span>}
                    <span className="flex-1" />
                    {hasRealId(v.insurance) ? <button type="button" onClick={() => onInherit(v, !!sec)} className="text-[11px] font-bold rounded-[6px] px-2 py-1 border bg-white" style={{ borderColor: HAIR, color: INK }} data-testid={`insp-inherit-${v.id}`}>{sec ? "Inherit 1° + 2°" : "Inherit"}</button> : null}
                  </div>
                );
              })}
            </>
          ) : null}
          <div className="text-[11px] mt-2" style={{ color: INDIGO }}>See all {g.claims.length} claims in Flat</div>
        </div>
      ) : null}
    </div>
  );
}

// ── main panel ──────────────────────────────────────────────────────────────
export default function InsurancePanel({ note: initialNote, clientId, onClose, onUpdated }) {
  const [note, setNote] = useState(initialNote);
  const noteId = note?.id;
  const cid = clientId || note?.client_id;
  const primary = note?.insurance;
  const secondary = note?.insurance_secondary;
  const st = stateOf(primary);

  const [secondaryMode, setSecondaryMode] = useState(false);
  const [changing, setChanging] = useState(false); // user clicked Change on an ok primary
  const [picked, setPicked] = useState(null);       // { payer_id, payer_name, source, chip }
  const [member, setMember] = useState("");
  const [group, setGroup] = useState("");
  const [inheritedSec, setInheritedSec] = useState(null); // { payer_id, payer_name, member_id, date }
  const [teach, setTeach] = useState(true);
  const [busy, setBusy] = useState(false);
  const editorRef = useRef(null);

  const editing = secondaryMode || changing || st !== "ok";

  // prefill member id on open
  useEffect(() => {
    const seed = note?.member_id || primary?.member_id || note?.insurance_on_file?.member_id || "";
    setMember(seed); setGroup(primary?.group_number || "");
  }, [noteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = useCallback(async () => {
    try { const n = await getNote(noteId); if (n) { setNote((p) => ({ ...p, ...n })); onUpdated?.(n); return n; } } catch { /* keep */ }
    onUpdated?.(null); return null;
  }, [noteId, onUpdated]);

  const visitCpts = useMemo(() => [...new Set((note?.cpt_codes || []).map((c) => c.code).filter(Boolean))], [note]);

  const fill = (p) => {
    setPicked({ payer_id: p.payer_id, payer_name: p.payer_name, source: p.source, chip: p.chip });
    if (p.member_id != null) setMember(p.member_id || "");
    if (p.group_number != null) setGroup(p.group_number || "");
    setInheritedSec(null);
    setTimeout(() => editorRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }), 30);
  };
  const onInherit = (v, withSecondary) => {
    setChanging(true);
    setPicked({ payer_id: v.insurance.payer_id, payer_name: v.insurance.payer_name, source: "inherit", chip: `Inherited from the ${fmtDate(v.date_of_service)} visit` });
    setMember(v.insurance.member_id || "");
    setGroup(v.insurance.group_number || "");
    setInheritedSec(withSecondary && v.insurance_secondary?.payer_name ? { payer_id: v.insurance_secondary.payer_id, payer_name: v.insurance_secondary.payer_name, member_id: v.insurance_secondary.member_id, date: v.date_of_service } : null);
    setTimeout(() => editorRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }), 30);
  };

  const closeEditor = () => { setChanging(false); setSecondaryMode(false); setPicked(null); setInheritedSec(null); setMember(primary?.member_id || note?.member_id || note?.insurance_on_file?.member_id || ""); setGroup(primary?.group_number || ""); };
  const enterSecondary = () => { setSecondaryMode(true); setChanging(false); setPicked(null); setInheritedSec(null); setMember(secondary?.member_id || ""); setGroup(secondary?.group_number || ""); };

  const doSave = async () => {
    if (!picked?.payer_id || picked.payer_id === "MISSING") { toast.error("Pick a payer from the list to match it. A name alone can't be saved."); return; }
    if (!member) { toast.error("Add the Member ID."); return; }
    setBusy(true);
    const seq = secondaryMode ? 2 : 1;
    try {
      const body = { payer_id: picked.payer_id, payer_name: picked.payer_name, member_id: member, group_number: group || "", sequence: seq };
      if (st === "needs_id" && !secondaryMode && teach) body.teach_alias = primary?.payer_name;
      const res = await patchInsurance(noteId, body);
      let secRes = null;
      if (!secondaryMode && inheritedSec) {
        try { secRes = await patchInsurance(noteId, { payer_id: inheritedSec.payer_id, payer_name: inheritedSec.payer_name, member_id: inheritedSec.member_id || "", sequence: 2 }); }
        catch { toast.error("Primary saved. Secondary didn't save. Try again."); }
      }
      const vu = res?.visits_updated || 0;
      const bits = [`Primary set to ${picked.payer_name}`];
      if (secRes) bits.push(`secondary ${inheritedSec.payer_name}`);
      else if (secondaryMode) bits[0] = `Secondary set to ${picked.payer_name}`;
      if (vu) bits.push(`${vu} other visits updated`);
      if (body.teach_alias) bits.push(`revQ will match "${body.teach_alias}" next time`);
      toast.success(bits.join(" · "));
      closeEditor();
      await refetch();
    } catch (e) { toast.error(describeError(e)); } finally { setBusy(false); }
  };

  // heading + lead
  const cardPayer = note?.insurance_on_file?.payer_name;
  let heading = "Add primary insurance", lead = "";
  if (secondaryMode) { heading = "Add secondary insurance"; lead = `Separate from primary (${primary?.payer_name || "none"}). Saved as the second payer on this claim.`; }
  else if (changing && st === "ok") { heading = "Change primary insurance"; lead = `Currently ${primary?.payer_name}. Pick a different payer, or use one from the payer history.`; }
  else if (st === "needs_id") { heading = `Match "${primary?.payer_name}" to a payer`; lead = "This name was read from the patient's insurance, but it isn't matched to a payer yet, so the claim can't go out. Match it once; revQ remembers it for next time."; }
  else if (cardPayer) { lead = `This visit has no insurance. The card on file reads "${cardPayer}": pick the matching payer below, or use one from the payer history.`; }
  else { lead = "This visit has no insurance. Search the payer, or use one from the payer history."; }

  const saveLabel = secondaryMode ? "Save secondary" : inheritedSec ? "Save primary + secondary" : "Save primary";
  const canSave = !!picked?.payer_id && !!member && !busy;
  const chip = st === "none" ? "No insurance" : st === "needs_id" ? "No payer match" : null;

  return (
    <div className="insp bg-white rounded-[14px] border" style={{ borderColor: HAIR }} data-testid="insurance-panel">
      <style>{PANEL_CSS}</style>
      <div className="flex items-center gap-2 px-3.5 py-2.5" style={{ background: "#FBFBFD", borderBottom: `1px solid ${HAIR}` }}>
        <Shield size={15} style={{ color: INK }} />
        <span className="text-[14px] font-bold" style={{ color: INK }}>{secondaryMode ? "Secondary insurance" : "Insurance"}</span>
        {chip && !secondaryMode ? <span className="text-[10.5px] font-bold rounded-full px-2 py-0.5 inline-flex items-center gap-1" style={{ background: AMBERBG, color: AMBER }} data-testid="insp-chip">{chip}</span> : null}
        <span className="flex-1" />
        {secondaryMode ? <button type="button" onClick={closeEditor} className="text-[12px] font-semibold" style={{ color: INDIGO }} data-testid="insp-back-primary">‹ Back to primary</button> : null}
        <button type="button" onClick={onClose} className="text-[12px] font-semibold rounded-[8px] px-2.5 h-[28px] border bg-white inline-flex items-center gap-1" style={{ borderColor: HAIR, color: INK }} data-testid="insp-close"><X size={13} />Close</button>
      </div>

      <div className="insp-b">
        <CardColumn clientId={cid} mrn={note?.mrn} patientName={note?.patient_name} editing={editing} onScanFill={{ noteId, fill: (p) => fill(p) }} />

        <div className="insp-main" ref={editorRef}>
          {editing ? (
            <div data-testid="insp-editor">
              <h3 className="text-[17px] font-bold" style={{ color: INK }}>{heading}</h3>
              {lead ? <p className="text-[13px] mt-1" style={{ color: SUB, maxWidth: 620 }}>{lead}</p> : null}

              {!picked ? (
                <div className="mt-3"><PayerSearch prefill={secondaryMode ? "" : st === "needs_id" ? primary?.payer_name : cardPayer || ""} onPick={(p) => fill({ ...p })} /></div>
              ) : (
                <div className="mt-3">
                  <div className="inline-flex items-center gap-2 rounded-[9px] border px-3 py-2" style={{ borderColor: HAIR, background: "#fff" }} data-testid="insp-picked">
                    {inheritedSec ? <span className="text-[10px] font-bold rounded-[5px] px-1.5 py-0.5" style={{ background: "#EEF0FF", color: INDIGO }}>1°</span> : null}
                    <span className="text-[13px] font-semibold" style={{ color: INK }}>{picked.payer_name}</span>
                    <span className="text-[11px]" style={{ color: SUB, fontFamily: MONO }}>{picked.payer_id}</span>
                    {picked.chip ? <span className="text-[10px] rounded-full px-2 py-0.5" style={{ background: SEL, color: INDIGO }}>{picked.chip}</span> : null}
                    <button type="button" onClick={() => { setPicked(null); setInheritedSec(null); }} className="ml-1" style={{ color: GREY }} data-testid="insp-picked-clear"><X size={13} /></button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 mt-3" style={{ maxWidth: 420 }}>
                <div>
                  <label className="text-[10.5px] font-semibold uppercase tracking-[.04em]" style={{ color: GREY }}>Member ID</label>
                  <input value={member} onChange={(e) => setMember(e.target.value)} className="w-full mt-1 text-[13px] border rounded-[8px] px-2.5 py-1.5 bg-white" style={{ borderColor: HAIR, fontFamily: MONO }} data-testid="insp-member" />
                </div>
                <div>
                  <label className="text-[10.5px] font-semibold uppercase tracking-[.04em]" style={{ color: GREY }}>Group <span style={{ color: FAINT, fontWeight: 400 }}>· optional</span></label>
                  <input value={group} onChange={(e) => setGroup(e.target.value)} className="w-full mt-1 text-[13px] border rounded-[8px] px-2.5 py-1.5 bg-white" style={{ borderColor: HAIR }} data-testid="insp-group" />
                </div>
              </div>

              {inheritedSec ? (
                <div className="mt-3" data-testid="insp-inherited-sec">
                  <div className="inline-flex items-center gap-2 rounded-[9px] border px-3 py-2" style={{ borderColor: HAIR, background: "#fff" }}>
                    <span className="text-[10px] font-bold rounded-[5px] px-1.5 py-0.5" style={{ background: "#EEF0FF", color: INDIGO }}>2°</span>
                    <span className="text-[13px] font-semibold" style={{ color: INK }}>{inheritedSec.payer_name}</span>
                    <span className="text-[11px]" style={{ color: SUB, fontFamily: MONO }}>{inheritedSec.payer_id}</span>
                    {inheritedSec.member_id ? <span className="text-[11px]" style={{ color: SUB }}>member {inheritedSec.member_id}</span> : null}
                    <span className="text-[10px] rounded-full px-2 py-0.5" style={{ background: SEL, color: INDIGO }}>Inherited</span>
                    <button type="button" onClick={() => setInheritedSec(null)} className="ml-1" style={{ color: GREY }} data-testid="insp-sec-clear"><X size={13} /></button>
                  </div>
                  <div className="text-[11px] mt-1" style={{ color: FAINT }}>{secondary?.payer_name ? `Replaces the current secondary (${secondary.payer_name}). ` : ""}Saved as the second payer on this claim. Remove it to keep primary only.</div>
                </div>
              ) : null}

              {st === "needs_id" && !secondaryMode && picked ? (
                <label className="flex items-center gap-2 mt-3 text-[12.5px] cursor-pointer" style={{ color: SUB }} data-testid="insp-teach">
                  <input type="checkbox" checked={teach} onChange={(e) => setTeach(e.target.checked)} data-testid="insp-teach-cb" />
                  Remember “{primary?.payer_name}” as {picked.payer_name}
                </label>
              ) : null}

              <div className="flex items-center gap-3 mt-4">
                <button type="button" onClick={doSave} disabled={!canSave} className="text-[13px] font-bold text-white rounded-[9px] px-4 h-10 inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: INK }} data-testid="insp-save">{busy ? <Loader2 size={14} className="animate-spin" /> : null}{saveLabel}</button>
                {(changing || secondaryMode) ? <button type="button" onClick={closeEditor} className="text-[12.5px] font-semibold" style={{ color: GREY }} data-testid="insp-cancel">Cancel</button> : null}
              </div>
              <div className="text-[11px] mt-2" style={{ color: FAINT }}>Matching it also fixes other visits waiting on the same payer.</div>
            </div>
          ) : (
            <div data-testid="insp-viewer">
              <h3 className="text-[15px] font-bold mb-2" style={{ color: INK }}>On this claim</h3>
              <div className="rounded-[10px] border p-3 flex items-start gap-3" style={{ borderColor: HAIR }} data-testid="insp-view-primary">
                <span className="text-[10px] font-bold rounded-[5px] px-1.5 py-0.5" style={{ border: `1px solid ${HAIR}`, color: INK }}>1°</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-semibold" style={{ color: INK }}>{primary?.payer_name}</div>
                  <div className="text-[12px]" style={{ color: SUB }}>payer id {primary?.payer_id} · member {primary?.member_id || "—"}{primary?.group_number ? ` · group ${primary.group_number}` : ""}</div>
                </div>
                <button type="button" onClick={() => setChanging(true)} className="text-[12px] font-semibold rounded-[8px] px-3 py-1.5 border bg-white" style={{ borderColor: HAIR, color: INK }} data-testid="insp-change-primary">Change</button>
              </div>
              <div className="rounded-[10px] border p-3 flex items-start gap-3 mt-2" style={{ borderColor: HAIR }} data-testid="insp-view-secondary">
                <span className="text-[10px] font-bold rounded-[5px] px-1.5 py-0.5" style={{ border: `1px solid ${HAIR}`, color: INK }}>2°</span>
                <div className="flex-1 min-w-0">
                  {secondary?.payer_name ? <><div className="text-[13.5px] font-semibold" style={{ color: INK }}>{secondary.payer_name}</div><div className="text-[12px]" style={{ color: SUB }}>payer id {secondary.payer_id} · member {secondary.member_id || "—"}</div></>
                    : <div className="text-[13px]" style={{ color: GREY }}>No secondary insurance</div>}
                </div>
                <button type="button" onClick={enterSecondary} className="text-[12px] font-semibold rounded-[8px] px-3 py-1.5 border bg-white" style={{ borderColor: HAIR, color: INK }} data-testid="insp-add-secondary">{secondary?.payer_name ? "Change" : "Add secondary"}</button>
              </div>
              <div className="text-[11.5px] mt-2" style={{ color: FAINT }}>Open the payer history below to use or inherit a past payer for this visit.</div>
            </div>
          )}

          <PayerHistory clientId={cid} mrn={note?.mrn} excludeId={noteId} visitDos={note?.date_of_service} visitCpts={visitCpts}
            secondaryMode={secondaryMode} current={{ primary, secondary }} onUse={fill} onInherit={onInherit} />
        </div>
      </div>
    </div>
  );
}
